import { isDefined } from '../utils';
import { hexToOklch, oklchToHex } from './oklch';
import {
  resolveSurfaceChoice,
  SURFACE_TILES,
  type CustomSurfaceColor,
  type SurfaceChoice,
  type SurfaceVarName,
} from './surfaceTiles';
import { THEME_SETTING_KEYS } from './themeEffects';
import {
  AUTOMATIC_TEXT_THEME_VAR_NAMES,
  DERIVED_ONLY_THEME_VAR_NAMES,
  SURFACE_THEME_VAR_NAMES,
  THEME_VARS,
  type ThemeVarDefinition,
  type ThemeVarTransform,
} from './themeVars';

/**
 * Builds the runtime CSS var defaults straight from the catalog, verbatim —
 * this is the single source of truth AppThemeProvider's darkTheme is
 * generated from, so its output must stay byte-identical to what the catalog
 * declares (derived vars included, even where their stored defaultValue
 * doesn't actually match a recompute from their `from` source today).
 */
export function resolveThemeDefaults(): Record<string, string> {
  const defaults: Record<string, string> = {};
  for (const themeVar of THEME_VARS) {
    defaults[themeVar.varName] = themeVar.defaultValue;
  }
  return defaults;
}

/**
 * (`stripDerivedThemeVars` lived here: it dropped DERIVED vars from a stored
 * theme layer, because `mergeOrgTheme` recomputes a derived var from its source
 * unless the layer stores it explicitly — in which case an admin who changes
 * `primary-main` watches every `rgba(var(--mfw-palette-primary-mainChannel) / a)`
 * keep the previous brand colour. It had NO production caller: the one writer
 * that matters goes through `keepStorableTenantThemeVars`, which subsumes it by
 * keeping only the storable set, and migration 20260807120000 backfills the rows
 * that predate that. Removed rather than kept "just in case" — a second function
 * expressing half of the same rule is how the two drift apart.)
 */

/**
 * Builds the initial per-org theme SEED copied the first time a rebrand is
 * activated: for each editable var that is NOT derived (the two brand anchors,
 * `primary-main` and `accent-main`, which are the whole editable set), the tenant's override value
 * (when the already-parsed tenant theme provides a string for it) else the
 * catalog default. The output keys are exactly those source vars.
 *
 * A derived var is left out on purpose, and that holds even when the tenant
 * stores a value for it. Since rebrand v2 no editable var is derived — the text
 * ladder and `action-active` are not editable at all, and a tenant's legacy ink
 * for them is discarded by `mergeOrgTheme` — but the rule keeps its reason for
 * any editable var that ever is:
 *
 * - `mergeOrgTheme` never recomputes a var stored on the same layer as its
 *   source. Seeding a derived default alongside `primary-main` would pin it at
 *   whatever was current on activation day, instead of letting it follow the
 *   org's surface — the very class of default snapshot migration 20260820120000
 *   prunes from existing rows.
 * - A value chosen against the TENANT's surface does not carry over: a
 *   rebranded org's surface is brand-toned dark (`background-default` is
 *   `tone`-derived from `primary-main`) unless the tenant pinned its ladder.
 *   Promoting a tenant's manual ink verbatim measured 1.14:1 in review; a freshly
 *   derived ladder is readable by construction.
 *
 * Values are stored VERBATIM — tenant/catalog values may be `rgb(...)`,
 * `#FFF`, etc. This server-side seed is deliberately exempt from the FE
 * editor's hex rule (the editor normalizes later), so nothing is validated or
 * converted here.
 *
 * `tenantTheme` must already be a parsed object (or null/undefined) — the
 * defensive double-decode of the raw (double-encoded) TenantSettings JSONB
 * value lives on the BE side. Anything that isn't a plain object, and any
 * non-string value inside it, is ignored (falls back to the catalog default).
 */
export function buildRebrandThemeSeed(
  tenantTheme?: Record<string, unknown> | null,
): Record<string, string> {
  const tenantObject =
    isDefined(tenantTheme) &&
    typeof tenantTheme === 'object' &&
    !Array.isArray(tenantTheme)
      ? (tenantTheme as Record<string, unknown>)
      : null;

  const seed: Record<string, string> = {};
  for (const themeVar of THEME_VARS) {
    if (!themeVar.editable || isDefined(themeVar.derived)) {
      continue;
    }
    const tenantValue = isDefined(tenantObject)
      ? tenantObject[themeVar.varName]
      : undefined;
    seed[themeVar.varName] =
      typeof tenantValue === 'string' ? tenantValue : themeVar.defaultValue;
  }
  return seed;
}

const HEX_COLOR_RE = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const RGB_FUNC_RE =
  /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*[\d.]+\s*)?\)$/i;

/**
 * Converts [r, g, b] channels to lowercase 6-digit hex. Used to re-encode
 * parsed channels back to hex so both `shift` and `tone` transforms can
 * accept any source format (`#rgb`, `#rrggbb`, `rgb()`, `rgba()`), not just hex.
 */
const rgbToHex = (r: number, g: number, b: number) =>
  `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;

/**
 * Parses a derived var's `from` source into [r, g, b], accepting the formats
 * an editable var's value can actually take today: `#RGB`, `#RRGGBB`, and
 * `rgb()`/`rgba()` (source alpha, if any, is ignored — derived transforms
 * apply their own alpha). `background-default`'s canonical/default format is
 * rgba, not hex, so hex-only parsing here would throw on its own default.
 *
 * Throws on anything else (e.g. free text, `var()` references) — callers are
 * expected to treat that as "not a parseable color" and handle it (see
 * `mergeOrgTheme`'s swallow-and-keep-prior-value behavior below).
 *
 * Exported so a consumer that composites theme colours itself (the frontend's
 * brand-wash measurement) accepts exactly the formats the engine stores, rather
 * than a narrower hex-only parse of its own.
 */
export function parseSourceColorChannels(
  value: string,
): [number, number, number] {
  const trimmed = value.trim();

  const rgbMatch = RGB_FUNC_RE.exec(trimmed);
  if (isDefined(rgbMatch)) {
    return [Number(rgbMatch[1]), Number(rgbMatch[2]), Number(rgbMatch[3])];
  }

  const hexMatch = HEX_COLOR_RE.exec(trimmed);
  if (isDefined(hexMatch)) {
    const hex = hexMatch[1];
    const expanded =
      hex.length === 3
        ? hex
            .split('')
            .map((digit) => digit + digit)
            .join('')
        : hex;
    return [
      parseInt(expanded.slice(0, 2), 16),
      parseInt(expanded.slice(2, 4), 16),
      parseInt(expanded.slice(4, 6), 16),
    ];
  }

  throw new Error(
    `Unable to parse color for derived theme var transform: "${value}"`,
  );
}

/**
 * WCAG relative luminance of an sRGB color, in 0..1: gamma-expand (linearize)
 * each channel, then `L = 0.2126 R + 0.7152 G + 0.0722 B`.
 *
 * Exported for the brand check's direction text (`brandGrade.ts`), so the
 * formula keeps living in this one place.
 */
export function relativeLuminance(r: number, g: number, b: number): number {
  const linear = (channel: number) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

/**
 * Picks the readable text color for an (r,g,b) background: whichever of black
 * or white has the higher WCAG contrast ratio against it.
 *
 * This used to be a relative-luminance threshold at 0.5, but the luminance at
 * which black and white TIE is 0.179 — so every color in (0.179, 0.5] got
 * white when black was far better, scoring as low as 2.14:1 (`#66c2a5`) and
 * 2.28:1 (`#22C55E`, our own success green). Any tenant rebranding into that
 * band got unreadable button labels.
 *
 * The exact rule also bounds the result: at the crossover both options score
 * 4.58:1, so a derived contrastText can never fail WCAG AA for ANY color.
 *
 * Brand green `#28d771` (L≈0.502) still resolves to BLACK — 11.05:1 vs 1.90:1
 * — so the catalog's stored default keeps byte-parity.
 */
export function contrastColorForRgb(
  r: number,
  g: number,
  b: number,
): '#000000' | '#FFFFFF' {
  const luminance = relativeLuminance(r, g, b);
  const againstBlack = (luminance + 0.05) / 0.05;
  const againstWhite = 1.05 / (luminance + 0.05);
  return againstBlack >= againstWhite ? '#000000' : '#FFFFFF';
}

/**
 * Resolves the WCAG contrast color for any source color string the theme engine
 * accepts (`#RGB`, `#RRGGBB`, `rgb()`/`rgba()`). Throws on an unparseable source
 * (same contract as the derived transforms); the FE preview only ever passes a
 * validated 6-digit hex, so it never throws there. This is the single shared
 * helper the `contrast` transform AND the FE editor preview both call — the
 * formula lives in exactly one place.
 */
export function contrastTextColor(color: string): '#000000' | '#FFFFFF' {
  const [r, g, b] = parseSourceColorChannels(color);
  return contrastColorForRgb(r, g, b);
}

/**
 * The step of a colour ramp that can actually be READ on a given surface.
 *
 * Which one that is depends on the surface — `success` reads as its `light`
 * step on a dark ladder and its `darker` step on a light one — and some ramps
 * have no compliant step at all: the default brand green as small text on cream
 * tops out around 4.3:1 even at its darkest.
 *
 * So this measures rather than assumes, and takes the FIRST candidate that
 * clears `threshold` (candidates are passed lightest-first, so the lightest
 * readable step wins and the colour stays as close to the brand as it can be).
 * When none clears it returns `fallback` — plain body text, legible by
 * construction — and when there is no fallback, the best-contrast candidate,
 * because something readable-ish beats something arbitrary.
 *
 * An unparseable candidate is skipped, not thrown on: one bad value in a
 * tenant's palette must not take the whole page's colours down with it.
 *
 * This is the "guarantee the output" half of the accessibility story. The other
 * half — refusing a palette at save time — was deliberately NOT built: it would
 * lock out every row that already stores a failing colour, and it would punish
 * an owner for a pairing the app chose, not one they did.
 */
export function pickOnSurface(
  candidates: readonly string[],
  surface: string,
  options: { fallback?: string; threshold?: number } = {},
): string {
  const { fallback, threshold = 4.5 } = options;

  let best: { color: string; ratio: number } | null = null;
  for (const candidate of candidates) {
    let ratio: number;
    try {
      ratio = contrastRatio(candidate, surface);
    } catch {
      continue;
    }
    if (ratio >= threshold) {
      return candidate;
    }
    if (best === null || ratio > best.ratio) {
      best = { color: candidate, ratio };
    }
  }

  if (isDefined(fallback)) {
    return fallback;
  }
  return best === null ? surface : best.color;
}

/**
 * Which end of the ladder a surface sits on — the single rule three consumers
 * read: the MUI JS palette's `mode`, the `data-mfw-surface` flag the app writes
 * onto `:root` for CSS to key off, and (through `contrastTextColor`) the text
 * ladder's own derivation. Re-deriving it anywhere with a second luminance
 * formula is how those three come to disagree about the same background.
 *
 * Falls back to `'dark'` on anything unparseable: that is the platform default
 * ladder, so an unreadable value degrades to today's behaviour rather than
 * flipping the whole chrome on a typo.
 */
export function surfaceMode(color: string): 'dark' | 'light' {
  try {
    return contrastTextColor(color) === '#000000' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

/**
 * WCAG 2.x contrast ratio between two colors, in 1..21:
 * `(L_lighter + 0.05) / (L_darker + 0.05)`. Accepts any color string
 * `parseSourceColorChannels` handles (`#RGB`, `#RRGGBB`, `rgb()`/`rgba()`);
 * throws on unparseable input — same contract as `contrastTextColor`, FE
 * callers pre-validate. Advisory only (rebrand editor badge).
 */
export function contrastRatio(colorA: string, colorB: string): number {
  const [rA, gA, bA] = parseSourceColorChannels(colorA);
  const [rB, gB, bB] = parseSourceColorChannels(colorB);
  const luminanceA = relativeLuminance(rA, gA, bA);
  const luminanceB = relativeLuminance(rB, gB, bB);
  const lighter = Math.max(luminanceA, luminanceB);
  const darker = Math.min(luminanceA, luminanceB);
  return (lighter + 0.05) / (darker + 0.05);
}

/** `rgb()`/`rgba()` with a fourth component, which is its alpha. */
const ALPHA_FUNC_RE =
  /^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*([\d.]+)\s*\)$/i;

/**
 * `contrastRatio` of what a reader SEES: `foreground` laid over `background`
 * at its own alpha, per channel `Math.round(fg·a + bg·(1−a))`.
 *
 * `contrastRatio` ignores alpha, which is right for a solid fill and wrong for
 * ink: the secondary text is `rgba(255, 255, 255, 0.68)`, and on a red page
 * that reads 4.98:1 uncomposited but 3.00:1 as drawn. Anything measuring ink
 * the browser blends — text, hairlines, icons — measures it here. An opaque
 * colour (hex, `rgb()`, or `a >= 1`) is `contrastRatio` as it is; throws like
 * it on anything unparseable.
 */
export function compositeContrastRatio(
  foreground: string,
  background: string,
): number {
  const alphaMatch = ALPHA_FUNC_RE.exec(foreground.trim());
  const alpha = isDefined(alphaMatch) ? Number(alphaMatch[1]) : 1;
  if (!(alpha < 1)) {
    return contrastRatio(foreground, background);
  }
  const top = parseSourceColorChannels(foreground);
  const bottom = parseSourceColorChannels(background);
  const [r, g, b] = top.map((channel, i) =>
    Math.round(channel * alpha + bottom[i] * (1 - alpha)),
  );
  return contrastRatio(`rgb(${r}, ${g}, ${b})`, background);
}

/**
 * Whether one text color can carry a CTA painted with the brand gradient.
 *
 * The label sits over BOTH stops, so it must pass AA at the WORSE one — an
 * average is meaningless here. Measured across brand hues, a naive gradient
 * fails this for mid-lightness brands (red, magenta, indigo), which is why the
 * button falls back to a solid `primary-main` fill rather than shipping
 * unreadable text.
 */
export function brandGradientIsReadable(
  primaryMain: string,
  accentMain: string,
): boolean {
  const textColor = contrastTextColor(primaryMain);
  return (
    Math.min(
      contrastRatio(textColor, primaryMain),
      contrastRatio(textColor, accentMain),
    ) >= 4.5
  );
}

/**
 * Computes a derived var's value from its source. Exported because the rebrand
 * editor's live preview must produce EXACTLY what a save will produce — it used
 * to re-implement this switch in the frontend, so a transform added here and
 * forgotten there previewed one palette and stored another.
 *
 * Throws when `sourceValue` is not a parseable color; callers decide what that
 * means (see `mergeOrgTheme`'s swallow-and-keep-prior-value behavior).
 */
export function applyDerivedTransform(
  sourceValue: string,
  transform: ThemeVarTransform,
): string {
  const [r, g, b] = parseSourceColorChannels(sourceValue);

  switch (transform.kind) {
    case 'rgbChannel':
      return `${r} ${g} ${b}`;
    case 'alpha':
      return `rgba(${r}, ${g}, ${b}, ${transform.alpha})`;
    case 'contrast':
      return contrastColorForRgb(r, g, b);
    case 'shift': {
      const source = hexToOklch(rgbToHex(r, g, b));
      return oklchToHex({
        L: source.L + transform.dL,
        C: source.C * transform.fC,
        H: source.H,
      });
    }
    case 'tone': {
      const source = hexToOklch(rgbToHex(r, g, b));
      return oklchToHex({
        L: transform.L,
        // CAPPED BY THE SOURCE, not taken as an absolute. A neutral brand has
        // chroma 0 and therefore no hue at all — the angle `hexToOklch` reports
        // for it is a conversion artifact (~89.9 for `#808080`, 0 for
        // `#000000`). Planting the recipe's absolute chroma at that angle
        // painted a grey brand's whole chrome brown and a black brand's maroon,
        // silently, for any org owner who picked a neutral colour.
        //
        // The cap generalizes past the exactly-achromatic case: a surface toned
        // from the brand must never be MORE saturated than the brand, whether
        // the source is a pure grey or a near-grey whose hue is quantization
        // noise. Every chromatic brand is far above these recipes (the shipped
        // `#28d771` carries 0.196 against a 0.017-0.036 ladder), so no existing
        // value moves — `theme-derivation.spec.ts` pins that both ways.
        C: Math.min(transform.C, source.C),
        H: source.H,
      });
    }
  }
}

/**
 * Orders derived vars so a var's source is always processed BEFORE the vars
 * derived from it, keyed on chain depth: 0 for a var whose source is not itself
 * derived, +1 per hop towards a non-derived root. Ties keep the input order, so
 * the result is deterministic.
 *
 * The recompute loop in `mergeOrgTheme` needs this because derivation is a
 * CHAIN, not a single hop: `primary-main -> background-default -> {dim-10,
 * dim-100, transparent}`. `THEME_VARS` is authored in `varName` order, which
 * happens to put `background-default` ahead of its own dependents — an accident
 * that a rename (or a new derived var sorting before its source) would silently
 * revoke. Depth removes the dependence on it.
 *
 * A cycle is impossible in the catalog today and cheap to guard: the walk stops
 * at the first var it has already visited rather than looping forever.
 *
 * Exported so the ordering can be proven independently of the catalog's own
 * authoring order — feed it a REVERSED catalog and every source must still come
 * out ahead of its dependents.
 */
export function orderDerivedVarsBySourceDepth(
  themeVars: readonly ThemeVarDefinition[],
): ThemeVarDefinition[] {
  const sourceByVarName = new Map<string, string>();
  for (const themeVar of themeVars) {
    if (isDefined(themeVar.derived)) {
      sourceByVarName.set(themeVar.varName, themeVar.derived.from);
    }
  }

  const depthOf = (varName: string) => {
    const visited = new Set<string>([varName]);
    let current = varName;
    let depth = 0;
    for (;;) {
      const from = sourceByVarName.get(current);
      if (!isDefined(from) || visited.has(from)) {
        return depth;
      }
      visited.add(from);
      current = from;
      depth += 1;
    }
  };

  return themeVars
    .filter((themeVar) => isDefined(themeVar.derived))
    .map((themeVar, index) => ({
      depth: depthOf(themeVar.varName),
      index,
      themeVar,
    }))
    .sort((a, b) => a.depth - b.depth || a.index - b.index)
    .map((entry) => entry.themeVar);
}

/** The catalog's derived vars, sources first — computed once, see above. */
const DERIVED_VARS_SOURCE_FIRST = orderDerivedVarsBySourceDepth(THEME_VARS);

const SURFACE_VAR_NAMES = new Set(SURFACE_THEME_VAR_NAMES);

/** The surface vars with their catalog derivation — `brand-dark`'s recipe. */
const SURFACE_VARS = THEME_VARS.filter((themeVar) =>
  SURFACE_VAR_NAMES.has(themeVar.varName),
);

const SETTING_KEYS = new Set(THEME_SETTING_KEYS);

const TEXT_VAR_NAMES = new Set(AUTOMATIC_TEXT_THEME_VAR_NAMES);

const DERIVED_ONLY_VAR_NAMES = new Set(DERIVED_ONLY_THEME_VAR_NAMES);

/**
 * The tenant layer as the merge reads it: colours only. The non-colour setting
 * keys (`THEME_SETTING_KEYS`) are dropped — the background and the calendar mode
 * are org/corp-only, so a stray tenant key must neither pick one nor reach the
 * colour record, and `glow` is read from the RAW tenant record by `resolveGlow`,
 * never from the merged palette. This is the one place the tenant layer is
 * read, so it is the one place that has to know.
 *
 * Also without the five text/action vars (`AUTOMATIC_TEXT_THEME_VAR_NAMES`):
 * text is derived from the effective surface and nothing else, so ink a tenant
 * stored before rebrand v2 must neither show nor outrank the derivation. Left in,
 * it would sit at the tenant layer and the surface derivation could never move
 * it — white ink chosen for a dark ladder would stay on an org's light tile.
 *
 * Also without the derived-only vars (`DERIVED_ONLY_THEME_VAR_NAMES`), for the
 * same reason: the old tenant page stored its admin's whole resolved palette,
 * so a row may hold a surface-tint channel nobody chose, and at the tenant
 * layer it would outrank the derivation for a tenant that pinned a light
 * ladder of its own.
 *
 * Also without the tenant's surface pin once the org chose a background (a tile
 * or its own colour): that is an explicit choice, and it wins.
 */
function tenantLayerOf(
  tenantTheme: Record<string, string> | null | undefined,
  dropSurfaces: boolean,
) {
  const layer: Record<string, string> = {};
  if (!isDefined(tenantTheme)) {
    return layer;
  }
  for (const [varName, value] of Object.entries(tenantTheme)) {
    if (
      SETTING_KEYS.has(varName) ||
      TEXT_VAR_NAMES.has(varName) ||
      DERIVED_ONLY_VAR_NAMES.has(varName) ||
      (dropSurfaces && SURFACE_VAR_NAMES.has(varName))
    ) {
      continue;
    }
    layer[varName] = value;
  }
  return layer;
}

/**
 * How an org's OWN background colour becomes the five surfaces — frozen once
 * released: a stored colour must keep rendering the same ladder, so changing a
 * number here moves every org that picked one.
 *
 * Per mode, each of `paper`, `neutral` and `table1` is a lightness step `dL`
 * and a chroma factor `fC` applied to the colour in OKLCH (hue is kept).
 * `default` is the colour itself and `table-0` repeats `paper` (light) or
 * `neutral` (dark), like every curated tile.
 *
 * Light steps lift the card (`paper`) and sink the wells (`neutral`,
 * `table1`); dark steps lift everything off the page, the further the more
 * raised the surface. A curated tile's own page colour, run through it, lands
 * within 2 per RGB channel of that tile's curated ladder
 * (`custom-surface.spec.ts` pins that).
 */
export const CUSTOM_SURFACE_RECIPE = {
  light: {
    paper: { dL: 0.025, fC: 0.6 },
    neutral: { dL: -0.035, fC: 1.2 },
    table1: { dL: -0.015, fC: 1 },
  },
  dark: {
    paper: { dL: 0.05, fC: 1 },
    neutral: { dL: 0.11, fC: 1.05 },
    table1: { dL: 0.16, fC: 1.2 },
  },
} as const;

/**
 * The five surfaces an org's own background colour sets, by
 * `CUSTOM_SURFACE_RECIPE`: `L + dL` and `C * fC` at the colour's own hue, with
 * `L` capped at 1 and, on a dark colour, floored at the black tile's matching
 * surface so a near-black colour still gets a visible ladder — a custom
 * `#000000` is exactly the black tile.
 *
 * Mode is the one rule every consumer reads (`surfaceMode`), so the ladder and
 * the text derived from it cannot disagree. Written out here rather than run
 * through `applyDerivedTransform`: that transform has neither the floor nor
 * the cap.
 */
export function customSurfaceLadder(
  input: CustomSurfaceColor,
): Record<SurfaceVarName, string> {
  const black = SURFACE_TILES.black.ladder;
  if (!isDefined(black)) {
    throw new Error('customSurfaceLadder: the black tile has no ladder');
  }

  // A stored colour is lowercase, but the page colour here is stored verbatim
  // as `default`, so a caller's case must not leak into it.
  const color = input.toLowerCase() as CustomSurfaceColor;
  const mode = surfaceMode(color);
  const source = hexToOklch(color);
  const recipe = CUSTOM_SURFACE_RECIPE[mode];

  const surface = (
    { dL, fC }: { dL: number; fC: number },
    floorVar: SurfaceVarName,
  ) => {
    const stepped = source.L + dL;
    const floored =
      mode === 'dark'
        ? Math.max(stepped, hexToOklch(black[floorVar]).L)
        : stepped;
    return oklchToHex({
      L: Math.min(floored, 1),
      C: source.C * fC,
      H: source.H,
    });
  };

  const paper = surface(recipe.paper, '--mfw-palette-background-paper');
  const neutral = surface(recipe.neutral, '--mfw-palette-background-neutral');
  const table1 = surface(recipe.table1, '--mfw-palette-background-table-1');

  return {
    '--mfw-palette-background-default': color,
    '--mfw-palette-background-paper': paper,
    '--mfw-palette-background-neutral': neutral,
    '--mfw-palette-background-table-0': mode === 'light' ? paper : neutral,
    '--mfw-palette-background-table-1': table1,
  };
}

/**
 * The five surfaces an explicit background choice puts on the ORG layer: a
 * fixed tile's ladder, an org's own colour's ladder (`customSurfaceLadder`),
 * or for `brand-dark` the ladder toned from the EFFECTIVE brand — the org's
 * `primary-main`, else the inherited tenant/default one.
 *
 * `brand-dark` is computed here rather than left to the recompute loop because
 * the loop only repaints a var whose source moved at a HIGHER layer. With an
 * inherited brand nothing above the surfaces moved it (a pin riding in
 * `defaults` is the extreme case), so the loop would keep the very pin the org
 * explicitly chose against.
 *
 * A surface whose source cannot be parsed is left out, so it keeps its prior
 * value — the loop's own rule for a bad source.
 */
function explicitSurfaces(
  choice: SurfaceChoice | undefined,
  brandSource: Record<string, string>,
) {
  if (!isDefined(choice)) {
    return {};
  }
  if (choice.kind === 'custom') {
    return customSurfaceLadder(choice.color);
  }
  const fixed = SURFACE_TILES[choice.id].ladder;
  if (isDefined(fixed)) {
    return { ...fixed };
  }

  const surfaces: Record<string, string> = {};
  for (const { varName, derived } of SURFACE_VARS) {
    if (!isDefined(derived)) {
      continue;
    }
    const sourceValue = brandSource[derived.from];
    if (!isDefined(sourceValue)) {
      continue;
    }
    try {
      surfaces[varName] = applyDerivedTransform(sourceValue, derived.transform);
    } catch {
      // Unparseable brand — keep the prior surface, see above.
    }
  }
  return surfaces;
}

/**
 * Merges tenant and org theme overrides on top of the catalog defaults:
 * `{...defaults, ...tenantLayer, ...orgLayer}`.
 *
 * `tenantLayer` is the tenant theme minus the non-colour setting keys
 * (`THEME_SETTING_KEYS`) and minus the five text/action vars
 * (`AUTOMATIC_TEXT_THEME_VAR_NAMES`) and the derived-only vars
 * (`DERIVED_ONLY_THEME_VAR_NAMES`) — otherwise as-is (a legacy row may still
 * hold other raw derived values) — and minus its surface pin when the org
 * explicitly chose a background (a tile or its own colour).
 * `orgLayer` is the org theme filtered down to only the editable vars (any
 * other keys it contains are ignored, which includes the text/action vars),
 * plus the five surfaces of an explicit background, see below. Either way,
 * no setting key — `glow`, the background, the calendar mode — reaches the
 * result: each is read from the RAW record by its own resolver.
 *
 * Text is never chosen (rebrand v2): both layers discard a stored value for the
 * five text/action vars here, at the boundary, so the ladder is always derived
 * from the effective surface and a legacy value in a row changes nothing — with
 * or without the migration that strips it having run.
 *
 * After that merge, each derived var (the brand ramps, the surface ladder,
 * contrastText/mainChannel/dim-10/dim-100/transparent) is recomputed from its
 * `from` source under a single precedence rule. Let `storedLayerOf(x)` be the
 * highest layer that SET `x` (org = 2, tenant = 1, catalog = 0), and
 * `effectiveLayerOf(x)` be `storedLayerOf(x)` raised to the layer of the value
 * `x` was last recomputed FROM. Recompute derived var V from merged source S
 * iff `effectiveLayerOf(S) > effectiveLayerOf(V)`.
 *
 * One rule instead of three, and it fixes the case the three got wrong: a
 * layer that sets BOTH a source and an editable derived var kept having its
 * explicit value overwritten by its own source change.
 *
 * The EFFECTIVE part is what makes a chain longer than one hop work. A var that
 * was recomputed rather than stored has `storedLayerOf === 0`, so under the
 * stored layer alone the chain `primary-main -> background-default -> {dim-10,
 * dim-100, transparent}` fired on the first hop and died on the second: a
 * rebranded org repainted every surface but kept the PREVIOUS brand inside its
 * dimmers and its `transparent` scrim. Carrying the source's layer forward
 * makes the recomputed `background-default` count as org-set for the vars
 * derived from it, which is what it is. Every row of decision D-5's table is
 * unaffected — for a var nothing recomputes, `effectiveLayerOf` IS
 * `storedLayerOf`.
 *
 * For the text/action vars no layer can ever set V — the org filter drops them
 * (they are not editable), the tenant layer strips them, and the write DTOs
 * reject them — so `storedLayerOf(V)` is 0, legacy rows included, and the
 * ladder follows whichever surface is effective.
 *
 * The one exception is the surface ladder (`SURFACE_THEME_VAR_NAMES`): a
 * STORED surface is never recomputed, whatever layer moved its source. Toned
 * from `primary-main` it is a tint of the brand, but stored it is a chosen
 * background — and an org's record stores no surface values of its own (only
 * a tile id or a colour, below), so recomputing a tenant's pin from an org's
 * brand did not let the org win, it erased the tenant's choice (a cream ladder
 * under an org brand of `#1e88e5` resolved to `#070d14`, while the org editor
 * previewed and graded the cream pin). A pin
 * keeps its stored layer, so what derives FROM it — the text ladder,
 * `dim-*`, `transparent` — follows the pin unless a layer stored those too.
 * Brand-attached vars (`mainChannel`, `contrastText`, the ramps) are not
 * surfaces and still follow the org's brand.
 *
 * Backgrounds (`surfaceTiles.ts`) are how an org DOES choose a surface. The
 * org record stores only a tile id or its own `#rrggbb` colour under
 * `THEME_SURFACE_TILE_KEY`, read here from the raw record (it is not an
 * editable var, so it never reaches the result), and resolved highest first:
 *
 * 1. a fixed tile or a custom colour — its five surfaces are placed on the ORG
 *    layer (a colour's by `customSurfaceLadder`), so they are stored surfaces
 *    like any pin: never recomputed, and the text ladder, dimmers and
 *    `transparent` derive from them;
 * 2. `brand-dark` — the ladder toned from the effective `primary-main`, placed
 *    on the org layer the same way, so it beats a tenant pin whichever layer
 *    the brand colour came from (see `explicitSurfaces`);
 * 3. no background (absent, or neither a catalog id nor a `#rrggbb`) —
 *    everything above, unchanged.
 *
 * An explicit background also drops the tenant's surface pin from the tenant
 * layer; the key itself is never read from (or passed through) the tenant
 * layer. No input record is mutated.
 */
export function mergeOrgTheme(
  defaults: Record<string, string>,
  tenantTheme?: Record<string, string> | null,
  orgTheme?: Record<string, string> | null,
): Record<string, string> {
  const editableVarNames = new Set(
    THEME_VARS.filter((themeVar) => themeVar.editable).map(
      (themeVar) => themeVar.varName,
    ),
  );

  const orgEditableOverrides: Record<string, string> = {};
  if (isDefined(orgTheme)) {
    for (const [varName, value] of Object.entries(orgTheme)) {
      if (editableVarNames.has(varName)) {
        orgEditableOverrides[varName] = value;
      }
    }
  }

  // The tile id or colour comes from the RAW org record — the editable filter
  // above has already dropped it.
  const surfaceChoice = resolveSurfaceChoice(orgTheme);
  const tenantLayer = tenantLayerOf(tenantTheme, isDefined(surfaceChoice));
  const orgLayer: Record<string, string> = {
    ...orgEditableOverrides,
    ...explicitSurfaces(surfaceChoice, {
      ...defaults,
      ...tenantLayer,
      ...orgEditableOverrides,
    }),
  };

  const merged: Record<string, string> = {
    ...defaults,
    ...tenantLayer,
    ...orgLayer,
  };

  const storedLayerOf = (varName: string) => {
    if (Object.prototype.hasOwnProperty.call(orgLayer, varName)) return 2;
    if (Object.prototype.hasOwnProperty.call(tenantLayer, varName)) return 1;
    return 0;
  };

  // Raised entries only: a var absent here was never recomputed, so its
  // effective layer is exactly the layer that stored it.
  const recomputedLayer = new Map<string, number>();
  const effectiveLayerOf = (varName: string) =>
    recomputedLayer.get(varName) ?? storedLayerOf(varName);

  for (const themeVar of DERIVED_VARS_SOURCE_FIRST) {
    const { derived } = themeVar;
    if (!isDefined(derived)) {
      continue;
    }
    const sourceLayer = effectiveLayerOf(derived.from);
    if (sourceLayer <= effectiveLayerOf(themeVar.varName)) {
      continue;
    }
    // A stored surface is a chosen background, not a tint — see the doc above.
    if (
      SURFACE_VAR_NAMES.has(themeVar.varName) &&
      storedLayerOf(themeVar.varName) > 0
    ) {
      continue;
    }

    const sourceValue = merged[derived.from];
    if (!isDefined(sourceValue)) {
      continue;
    }

    try {
      merged[themeVar.varName] = applyDerivedTransform(
        sourceValue,
        derived.transform,
      );
      recomputedLayer.set(
        themeVar.varName,
        Math.max(storedLayerOf(themeVar.varName), sourceLayer),
      );
    } catch {
      // The winning source override isn't a parseable color — keep the
      // pre-recompute value AND its layer rather than let one bad override
      // break theming: the value did not move, so neither does its provenance.
    }
  }

  return merged;
}
